<script>
import { defineAsyncComponent, useTemplateRef } from 'vue';
import { mapGetters } from 'vuex';
import { useMediaQuery } from '@vueuse/core';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useWorkspaceV2 } from 'dashboard/composables/useWorkspaceV2';
import { useFocusMode } from 'dashboard/composables/useFocusMode';
import { useTrack } from 'dashboard/composables';
import keyboardEventListenerMixins from 'shared/mixins/keyboardEventListenerMixins';

import ReplyToMessage from './ReplyToMessage.vue';
import AttachmentPreview from 'dashboard/components/widgets/AttachmentsPreview.vue';
import ReplyTopPanel from 'dashboard/components/widgets/WootWriter/ReplyTopPanel.vue';
import PropertySuggestionModal from 'dashboard/components/widgets/conversation/PropertySuggestionModal.vue';
import ReplyEmailHead from './ReplyEmailHead.vue';
import ReplyBottomPanel from 'dashboard/components/widgets/WootWriter/ReplyBottomPanel.vue';
import CopilotReplyBottomPanel from 'dashboard/components/widgets/WootWriter/CopilotReplyBottomPanel.vue';
import ArticleSearchPopover from 'dashboard/routes/dashboard/helpcenter/components/ArticleSearch/SearchPopover.vue';
import CopilotEditorSection from './CopilotEditorSection.vue';
import MessageSignatureMissingAlert from './MessageSignatureMissingAlert.vue';
import ReplyBoxBanner from './ReplyBoxBanner.vue';
import MessagingWindowNotice from './MessagingWindowNotice.vue';
import QuotedEmailPreview from './QuotedEmailPreview.vue';
import { REPLY_EDITOR_MODES } from 'dashboard/components/widgets/WootWriter/constants';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import AudioRecorder from 'dashboard/components/widgets/WootWriter/AudioRecorder.vue';
import { AUDIO_FORMATS } from 'shared/constants/messages';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { CMD_AI_ASSIST } from 'dashboard/helper/commandbar/events';
import {
  getMessageVariables,
  getUndefinedVariablesInMessage,
  replaceVariablesInMessage,
} from '@chatwoot/utils';
import WhatsappTemplates from './WhatsappTemplates/Modal.vue';
import ContentTemplates from './ContentTemplates/ContentTemplatesModal.vue';
import { MESSAGE_MAX_LENGTH } from 'shared/helpers/MessageTypeHelper';
import inboxMixin, { INBOX_FEATURES } from 'shared/mixins/inboxMixin';
import { trimContent, debounce, getRecipients } from '@chatwoot/utils';
import wootConstants from 'dashboard/constants/globals';
import {
  extractQuotedEmailText,
  buildQuotedEmailHeader,
  truncatePreviewText,
  appendQuotedTextToMessage,
} from 'dashboard/helper/quotedEmailHelper';
import {
  CONVERSATION_EVENTS,
  CAPTAIN_EVENTS,
} from '../../../helper/AnalyticsHelper/events';
import fileUploadMixin from 'dashboard/mixins/fileUploadMixin';
import {
  appendSignature,
  removeSignature,
  getEffectiveChannelType,
} from 'dashboard/helper/editorHelper';
import {
  materializeCannedFilesForComposer,
  buildWhatsAppMultipleMessagesPayload,
  filterCannedUploadsForChannel,
} from 'dashboard/helper/cannedResponseHelper';
import {
  suggestionIdFromText,
  buildCaptainSuggestionAttribute,
  assistantIdFromConversation,
} from 'dashboard/helper/captainSuggestionTelemetry';
import { useCopilotReply } from 'dashboard/composables/useCopilotReply';
import { useKbd } from 'dashboard/composables/utils/useKbd';
import { useSmallScreen } from 'dashboard/composables/useSmallScreen';
import { isFileTypeAllowedForChannel } from 'shared/helpers/FileHelper';
import AiReportModal from './AiReportModal.vue';
import AiAgentSuggestionsAPI, {
  SUGGESTION_POLLING_TIMEOUT,
} from 'dashboard/api/aiAgentSuggestions';

import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { contaLocalStorage } from 'dashboard/helper/accountScopedStorage';
import { emitter } from 'shared/helpers/mitt';

// SiteUp (Lia): os dois únicos inventários que o PropertySuggestionModal conhece
// ('lia' = inventário próprio, 'todas' = radar).
const PROPERTY_SOURCES = ['lia', 'todas'];

const EmojiIconPicker = defineAsyncComponent(
  () =>
    import('dashboard/components-next/emoji-icon-picker/EmojiIconPicker.vue')
);

export default {
  components: {
    AiReportModal,
    ArticleSearchPopover,
    AttachmentPreview,
    AudioRecorder,
    ReplyBoxBanner,
    MessagingWindowNotice,
    EmojiIconPicker,
    MessageSignatureMissingAlert,
    ReplyBottomPanel,
    ReplyEmailHead,
    ReplyToMessage,
    ReplyTopPanel,
    ContentTemplates,
    WhatsappTemplates,
    WootMessageEditor,
    QuotedEmailPreview,
    CopilotEditorSection,
    CopilotReplyBottomPanel,
    PropertySuggestionModal,
  },
  mixins: [inboxMixin, fileUploadMixin, keyboardEventListenerMixins],
  emits: ['toggleEditorSize'],
  setup() {
    const {
      uiSettings,
      isEditorHotKeyEnabled,
      fetchSignatureFlagFromUISettings,
      setQuotedReplyFlagForInbox,
      fetchQuotedReplyFlagFromUISettings,
    } = useUISettings();

    const replyEditor = useTemplateRef('replyEditor');
    const messageEditor = useTemplateRef('messageEditor');
    const copilot = useCopilotReply();
    const shortcutKey = useKbd(['$mod', '+', 'enter']);
    const { isFocusModeEnabled } = useFocusMode();
    const { isSmallScreen } = useSmallScreen();
    const { isWorkspaceV2 } = useWorkspaceV2();
    const isShortDesktop = useMediaQuery(
      '(min-width: 768px) and (max-height: 850px)'
    );

    return {
      uiSettings,
      isEditorHotKeyEnabled,
      fetchSignatureFlagFromUISettings,
      setQuotedReplyFlagForInbox,
      fetchQuotedReplyFlagFromUISettings,
      replyEditor,
      messageEditor,
      copilot,
      shortcutKey,
      isFocusModeEnabled,
      isSmallScreen,
      isWorkspaceV2,
      isShortDesktop,
    };
  },
  data() {
    return {
      message: '',
      propertySource: null,
      inReplyTo: {},
      isFocused: false,
      // Ponteiro apertado DENTRO do composer. Sem isto, o mousedown num botão
      // faz o editor perder o foco, o painel colapsa/vira inert e o clique
      // nunca chega no botão — todo botão do composer virava morto.
      isPointerInside: false,
      showEmojiPicker: false,
      attachedFiles: [],
      isRecordingAudio: false,
      recordingAudioState: '',
      recordingAudioDurationText: '',
      replyType: REPLY_EDITOR_MODES.REPLY,
      bccEmails: '',
      ccEmails: '',
      toEmails: '',
      doAutoSaveDraft: () => {},
      showWhatsAppTemplatesModal: false,
      isSendingWhatsAppTemplate: false,
      whatsAppTemplateRequestId: 0,
      showContentTemplatesModal: false,
      updateEditorSelectionWith: '',
      undefinedVariableMessage: '',
      showMentions: false,
      showUserMentions: false,
      showCannedMenu: false,
      showVariablesMenu: false,
      newConversationModalActive: false,
      showArticleSearchPopover: false,
      hasRecordedAudio: false,
      copilotAcceptedMessages: {},
      showCopilotReport: false,
      copilotReportText: '',
      captainSuggestion: null,
      shownCaptainSuggestion: null,
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      messageSignature: 'getMessageSignature',
      currentUser: 'getCurrentUser',
      lastEmail: 'getLastEmailInSelectedChat',
      globalConfig: 'globalConfig/get',
    }),
    currentContact() {
      const senderId = this.currentChat?.meta?.sender?.id;
      if (!senderId) return {};
      return this.$store.getters['contacts/getContact'](senderId);
    },
    shouldShowReplyToMessage() {
      return (
        this.inReplyTo?.id &&
        !this.isPrivate &&
        this.inboxHasFeature(INBOX_FEATURES.REPLY_TO) &&
        !this.is360DialogWhatsAppChannel &&
        !this.copilot.isActive.value
      );
    },
    showWhatsappTemplates() {
      // Eligible templates only (approved/supported). API inboxes stay compatible
      // because the filtered getter still reads additional_attributes.message_templates.
      const templates = this.$store.getters[
        'inboxes/getFilteredWhatsAppTemplates'
      ](this.inboxId);
      return !!(templates && templates.length) && !this.isPrivate;
    },
    showContentTemplates() {
      return this.isATwilioWhatsAppChannel && !this.isPrivate;
    },
    isPrivate() {
      if (
        this.currentChat.can_reply ||
        this.isAWhatsAppChannel ||
        this.isAPIInbox
      ) {
        return this.isOnPrivateNote;
      }
      return true;
    },
    hasMeaningfulEditorContent() {
      const body = this.message || '';
      // Only strip the signature when it's actually being auto-appended.
      // If the toggle is off, the agent's text might happen to match their
      // saved signature and we'd incorrectly treat it as empty.
      const shouldStripSignature =
        !this.isPrivate && this.sendWithSignature && !!this.messageSignature;
      if (!shouldStripSignature) return !!body.trim();
      const stripped = removeSignature(
        body,
        this.messageSignature,
        getEffectiveChannelType(this.channelType, this.inbox?.medium || '')
      );
      return !!stripped.trim();
    },
    isReplyRestricted() {
      return (
        !this.currentChat?.can_reply &&
        !(this.isAWhatsAppChannel || this.isAPIInbox)
      );
    },
    inboxId() {
      return this.currentChat.inbox_id;
    },
    inbox() {
      return this.$store.getters['inboxes/getInbox'](this.inboxId);
    },
    messagePlaceHolder() {
      if (this.isEditorDisabled) {
        if (this.isAWhatsAppChannel) {
          return this.$t('CONVERSATION.FOOTER.MESSAGING_RESTRICTED_WHATSAPP');
        }
        if (this.isAPIInbox) {
          return this.$t('CONVERSATION.FOOTER.MESSAGING_RESTRICTED_API');
        }
        return this.$t('CONVERSATION.FOOTER.MESSAGING_RESTRICTED');
      }
      if (this.isSmallScreen || this.isWorkspaceV2) {
        return this.isPrivate
          ? this.$t('CONVERSATION.FOOTER.PRIVATE_MSG_INPUT_MOBILE')
          : this.$t('CONVERSATION.FOOTER.MSG_INPUT_MOBILE');
      }
      return this.isPrivate
        ? this.$t('CONVERSATION.FOOTER.PRIVATE_MSG_INPUT')
        : this.$t('CONVERSATION.FOOTER.MSG_INPUT');
    },
    isMessageLengthReachingThreshold() {
      return this.message.length > this.maxLength - 50;
    },
    charactersRemaining() {
      return this.maxLength - this.message.length;
    },
    isReplyButtonDisabled() {
      if (this.isEditorDisabled) return true;
      if (this.isATwitterInbox) return true;
      if (this.hasAttachments || this.hasRecordedAudio) return false;

      return (
        this.isMessageEmpty ||
        this.message.length === 0 ||
        this.message.length > this.maxLength
      );
    },
    sender() {
      return {
        name: this.currentUser.name,
        thumbnail: this.currentUser.avatar_url,
      };
    },
    conversationType() {
      const { additional_attributes: additionalAttributes } = this.currentChat;
      const type = additionalAttributes ? additionalAttributes.type : '';
      return type || '';
    },
    maxLength() {
      if (this.isPrivate) {
        return MESSAGE_MAX_LENGTH.GENERAL;
      }
      if (this.isAFacebookInbox) {
        return MESSAGE_MAX_LENGTH.FACEBOOK;
      }
      if (this.isAnInstagramChannel) {
        return MESSAGE_MAX_LENGTH.INSTAGRAM;
      }
      if (this.isATelegramChannel) {
        return MESSAGE_MAX_LENGTH.TELEGRAM;
      }
      if (this.isATiktokChannel) {
        return MESSAGE_MAX_LENGTH.TIKTOK;
      }
      if (this.isATwilioWhatsAppChannel) {
        return MESSAGE_MAX_LENGTH.TWILIO_WHATSAPP;
      }
      if (this.isAWhatsAppCloudChannel) {
        return MESSAGE_MAX_LENGTH.WHATSAPP_CLOUD;
      }
      if (this.isASmsInbox) {
        return MESSAGE_MAX_LENGTH.TWILIO_SMS;
      }
      if (this.isAnEmailChannel) {
        return MESSAGE_MAX_LENGTH.EMAIL;
      }
      if (this.isATwilioSMSChannel) {
        return MESSAGE_MAX_LENGTH.TWILIO_SMS;
      }
      if (this.isAWhatsAppChannel) {
        return MESSAGE_MAX_LENGTH.WHATSAPP_CLOUD;
      }
      return MESSAGE_MAX_LENGTH.GENERAL;
    },
    showFileUpload() {
      const { image_send: imageSend } =
        this.currentChat?.additional_attributes?.tiktok_capabilities ?? {};
      const tiktokAttachmentSupported = imageSend ?? true;

      return (
        this.isAWebWidgetInbox ||
        this.isAFacebookInbox ||
        this.isAWhatsAppChannel ||
        this.isAPIInbox ||
        this.isAnEmailChannel ||
        this.isASmsInbox ||
        this.isATelegramChannel ||
        this.isALineChannel ||
        this.isAnInstagramChannel ||
        (this.isATiktokChannel && tiktokAttachmentSupported)
      );
    },
    replyButtonLabel() {
      let sendMessageText = this.$t('CONVERSATION.REPLYBOX.SEND');
      if (this.isPrivate) {
        sendMessageText = this.$t('CONVERSATION.REPLYBOX.CREATE');
      }
      const keyLabel = this.isEditorHotKeyEnabled('cmd_enter')
        ? `(${this.shortcutKey})`
        : '(↵)';
      return `${sendMessageText} ${keyLabel}`;
    },
    // Fonte única do estado "barra de modo aberta": CSS (.is-focused) e
    // acessibilidade (inert) leem daqui, senão um dos dois sai de sincronia.
    isModePanelVisible() {
      return (
        this.isFocused ||
        this.isPointerInside ||
        !!this.hasAttachments ||
        this.hasMeaningfulEditorContent
      );
    },
    isCompactMobileComposer() {
      return (
        this.isSmallScreen &&
        this.isWorkspaceV2 &&
        this.isDefaultEditorMode &&
        !this.showReplyHead &&
        !this.showAudioRecorderEditor &&
        !this.hasAttachments &&
        !this.shouldShowReplyToMessage &&
        !this.copilot.isActive.value
      );
    },
    isCompactDesktopComposer() {
      return (
        this.isShortDesktop &&
        this.isWorkspaceV2 &&
        this.isDefaultEditorMode &&
        !this.showReplyHead &&
        !this.showAudioRecorderEditor &&
        !this.hasAttachments &&
        !this.shouldShowReplyToMessage &&
        !this.copilot.isActive.value
      );
    },
    replyBoxClass() {
      return {
        'is-private': this.isPrivate,
        '!bg-white dark:!bg-[#17191c] !border-n-weak focus-within:!border-n-slate-7 !shadow-[0_8px_24px_rgba(0,0,0,0.08)] dark:!shadow-[0_8px_24px_rgba(0,0,0,0.16)]':
          this.isWorkspaceV2 && !this.isPrivate,
        '!mx-4 !mb-0': this.isWorkspaceV2 && !this.isSmallScreen,
        '!mx-4 !mb-[calc(1.5rem+env(safe-area-inset-bottom,0px))] shadow-lg':
          this.isSmallScreen && this.isWorkspaceV2,
        'grid grid-cols-[2.75rem_minmax(0,1fr)_auto] items-end gap-x-1 !p-1.5':
          this.isCompactMobileComposer || this.isCompactDesktopComposer,
        // SiteUp (T03-B): com o repouso compacto valendo também no desktop,
        // a barra de modo precisa continuar visível enquanto existe rascunho
        // — senão a pessoa escreve, o foco sai e o toggle Responder/Nota some
        // com o texto ainda na tela. É exatamente o cenário que a decisão
        // travada quer evitar (nota interna entregue ao cliente).
        'is-focused': this.isModePanelVisible,
        '!mx-0': this.isFocusModeEnabled && !this.isWorkspaceV2,
      };
    },
    hasAttachments() {
      return this.attachedFiles.length;
    },
    showAudioRecorder() {
      return !this.isOnPrivateNote && this.showFileUpload;
    },
    showAudioRecorderEditor() {
      return this.showAudioRecorder && this.isRecordingAudio;
    },
    isOnPrivateNote() {
      return this.replyType === REPLY_EDITOR_MODES.NOTE;
    },
    isOnExpandedLayout() {
      const {
        LAYOUT_TYPES: { CONDENSED },
      } = wootConstants;
      const { conversation_display_type: conversationDisplayType = CONDENSED } =
        this.uiSettings;
      return conversationDisplayType !== CONDENSED;
    },
    isMessageEmpty() {
      if (!this.message) {
        return true;
      }
      return !this.message.trim().replace(/\n/g, '').length;
    },
    showReplyHead() {
      return !this.isOnPrivateNote && this.isAnEmailChannel;
    },
    enableMultipleFileUpload() {
      return (
        this.isAnEmailChannel ||
        this.isAWebWidgetInbox ||
        this.isAPIInbox ||
        this.isAWhatsAppChannel ||
        this.isATelegramChannel
      );
    },
    isSignatureEnabledForInbox() {
      return !this.isPrivate && this.sendWithSignature;
    },
    isSignatureAvailable() {
      return !!this.messageSignature;
    },
    sendWithSignature() {
      return this.fetchSignatureFlagFromUISettings(this.channelType);
    },
    conversationId() {
      return this.currentChat.id;
    },
    conversationIdByRoute() {
      return this.conversationId;
    },
    editorStateId() {
      return `draft-${this.conversationIdByRoute}-${this.replyType}`;
    },
    audioRecordFormat() {
      if (this.isAWhatsAppCloudChannel) {
        return AUDIO_FORMATS.OGG;
      }
      if (this.isAWhatsAppChannel || this.isATelegramChannel) {
        return AUDIO_FORMATS.MP3;
      }
      if (this.isAPIInbox) {
        return AUDIO_FORMATS.MP3;
      }
      return AUDIO_FORMATS.WAV;
    },
    messageVariables() {
      const variables = getMessageVariables({
        conversation: this.currentChat,
        contact: this.currentContact,
        inbox: this.inbox,
      });
      return variables;
    },
    connectedPortalSlug() {
      const { help_center: portal = {} } = this.inbox;
      const { slug = '' } = portal;
      return slug;
    },
    quotedReplyPreference() {
      if (!this.isAnEmailChannel) {
        return false;
      }

      return !!this.fetchQuotedReplyFlagFromUISettings(this.channelType);
    },
    lastEmailWithQuotedContent() {
      if (!this.isAnEmailChannel) {
        return null;
      }

      const lastEmail = this.lastEmail;
      if (!lastEmail || lastEmail.private) {
        return null;
      }

      return lastEmail;
    },
    quotedEmailText() {
      return extractQuotedEmailText(this.lastEmailWithQuotedContent);
    },
    quotedEmailPreviewText() {
      return truncatePreviewText(this.quotedEmailText, 80);
    },
    shouldShowQuotedReplyToggle() {
      return this.isAnEmailChannel && !this.isOnPrivateNote;
    },
    shouldShowQuotedPreview() {
      return (
        this.shouldShowQuotedReplyToggle &&
        this.quotedReplyPreference &&
        !!this.quotedEmailText
      );
    },
    isDefaultEditorMode() {
      return !this.showAudioRecorderEditor && !this.copilot.isActive.value;
    },
    isEditorDisabled() {
      return (
        (this.isAWhatsAppChannel || this.isAPIInbox) &&
        !this.isOnPrivateNote &&
        !this.currentChat.can_reply
      );
    },
  },
  watch: {
    currentChat(conversation, oldConversation) {
      const { can_reply: canReply } = conversation;
      if (oldConversation && oldConversation.id !== conversation.id) {
        // Only update email fields when switching to a completely different conversation (by ID)
        // This prevents overwriting user input (e.g., CC/BCC fields) when performing actions
        // like self-assign or other updates that do not actually change the conversation context
        this.setCCAndToEmailsFromLastChat();
        // Reset Copilot editor state (includes cancelling ongoing generation)
        this.copilot.reset();
        this.clearCaptainSuggestionState();
        this.whatsAppTemplateRequestId += 1;
        this.isSendingWhatsAppTemplate = false;
        this.showWhatsAppTemplatesModal = false;
      }

      if (this.isOnPrivateNote) {
        return;
      }

      if (canReply || this.isAWhatsAppChannel || this.isAPIInbox) {
        this.replyType = REPLY_EDITOR_MODES.REPLY;
      } else {
        this.replyType = REPLY_EDITOR_MODES.NOTE;
      }

      this.fetchAndSetReplyTo();
    },
    // When moving from one conversation to another, the store may not have the
    // list of all the messages. A fetch is subsequently made to get the messages.
    // This watcher handles two main cases:
    // 1. When switching conversations and messages are fetched/updated, ensures CC/BCC fields are set from the latest OUTGOING/INCOMING email (not activity/private messages).
    // 2. Fixes and issue where CC/BCC fields could be reset/lost after assignment/activity actions or message mutations that did not represent a true email context change.
    inboxId(newInboxId, oldInboxId) {
      if (newInboxId !== oldInboxId) {
        this.whatsAppTemplateRequestId += 1;
        this.isSendingWhatsAppTemplate = false;
        this.showWhatsAppTemplatesModal = false;
      }
    },
    lastEmail: {
      handler(lastEmail) {
        if (!lastEmail) return;
        this.setCCAndToEmailsFromLastChat();
      },
      deep: true,
    },
    conversationIdByRoute(conversationId, oldConversationId) {
      if (conversationId !== oldConversationId) {
        this.setToDraft(oldConversationId, this.replyType);
        this.getFromDraft();
        this.resetRecorderAndClearAttachments();
      }
    },
    message() {
      // Autosave the current message draft.
      this.doAutoSaveDraft();
    },
    replyType(updatedReplyType, oldReplyType) {
      this.setToDraft(this.conversationIdByRoute, oldReplyType);
      this.getFromDraft();
    },
  },

  mounted() {
    this.getFromDraft();
    // Don't use the keyboard listener mixin here as the events here are supposed to be
    // working even if the editor is focussed.
    document.addEventListener('paste', this.onPaste);
    document.addEventListener('keydown', this.handleKeyEvents);
    this.setCCAndToEmailsFromLastChat();
    this.doAutoSaveDraft = debounce(
      () => {
        this.saveDraft(this.conversationIdByRoute, this.replyType);
      },
      500,
      true
    );

    this.fetchAndSetReplyTo();
    emitter.on(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, this.onReplyToMessage);

    // A hacky fix to solve the drag and drop
    // Is showing on top of new conversation modal drag and drop
    // TODO need to find a better solution
    emitter.on(
      BUS_EVENTS.NEW_CONVERSATION_MODAL,
      this.onNewConversationModalActive
    );
    emitter.on(BUS_EVENTS.INSERT_INTO_NORMAL_EDITOR, this.addIntoEditor);
    emitter.on(
      BUS_EVENTS.PREPARE_CONVERSATION_REPLY,
      this.prepareConversationReply
    );
    emitter.on(CMD_AI_ASSIST, this.executeCopilotAction);
    // SiteUp: sugestão do agente Captain da conta (botão roxo ✨) abre o painel do
    // copilot (Editar/Enviar/Reportar) em vez de cair direto no editor.
    emitter.on('aiAgentInsertSuggestion', this.onAiAgentSuggestion);
    // SiteUp: o menu ✨ só pede a geração; o ReplyBox gera com loading no painel (igual
    // ao Resumir), assim o menu pode fechar sem cancelar/perder a requisição.
    emitter.on('aiAgentGenerateSuggestion', this.onAiAgentGenerate);
    emitter.on(
      BUS_EVENTS.OPEN_WHATSAPP_TEMPLATES,
      this.openWhatsappTemplateModal
    );
    emitter.on(BUS_EVENTS.MOBILE_COMPOSER_ACTION, this.onMobileComposerAction);
    emitter.on(
      BUS_EVENTS.OPEN_PROPERTY_SUGGESTIONS,
      this.openPropertySuggestions
    );
  },
  unmounted() {
    window.removeEventListener('pointerup', this.onComposerPointerUp);
    window.removeEventListener('pointercancel', this.onComposerPointerUp);
    document.removeEventListener('paste', this.onPaste);
    document.removeEventListener('keydown', this.handleKeyEvents);
    emitter.off(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, this.onReplyToMessage);
    emitter.off(BUS_EVENTS.INSERT_INTO_NORMAL_EDITOR, this.addIntoEditor);
    emitter.off(
      BUS_EVENTS.PREPARE_CONVERSATION_REPLY,
      this.prepareConversationReply
    );
    emitter.off(
      BUS_EVENTS.NEW_CONVERSATION_MODAL,
      this.onNewConversationModalActive
    );
    emitter.off(CMD_AI_ASSIST, this.executeCopilotAction);
    emitter.off('aiAgentInsertSuggestion', this.onAiAgentSuggestion);
    emitter.off('aiAgentGenerateSuggestion', this.onAiAgentGenerate);
    emitter.off(
      BUS_EVENTS.OPEN_WHATSAPP_TEMPLATES,
      this.openWhatsappTemplateModal
    );
    emitter.off(BUS_EVENTS.MOBILE_COMPOSER_ACTION, this.onMobileComposerAction);
    emitter.off(
      BUS_EVENTS.OPEN_PROPERTY_SUGGESTIONS,
      this.openPropertySuggestions
    );
  },
  methods: {
    prepareConversationReply({ conversationId, message }) {
      if (
        Number(conversationId) !== Number(this.currentChat?.id) ||
        typeof message !== 'string'
      )
        return;
      this.setReplyMode(REPLY_EDITOR_MODES.REPLY);
      this.$nextTick(() => {
        if (Number(conversationId) !== Number(this.currentChat?.id)) return;
        this.message = message;
        this.onFocus();
      });
    },
    getDraftKey(
      conversationId = this.conversationIdByRoute,
      replyType = this.replyType
    ) {
      return `draft-${conversationId}-${replyType}`;
    },
    getCopilotAcceptedMessage(replyType = this.replyType) {
      const key = this.getDraftKey(this.conversationIdByRoute, replyType);
      return this.copilotAcceptedMessages[key] || '';
    },
    setCopilotAcceptedMessage(message, replyType = this.replyType) {
      const key = this.getDraftKey(this.conversationIdByRoute, replyType);
      this.copilotAcceptedMessages[key] = trimContent(
        message || '',
        this.maxLength
      );
    },
    clearCopilotAcceptedMessage(replyType = this.replyType) {
      const key = this.getDraftKey(this.conversationIdByRoute, replyType);
      delete this.copilotAcceptedMessages[key];
    },
    handleInsert(article) {
      const { url, title } = article;
      // Removing empty lines from the title
      const lines = title.split('\n');
      const nonEmptyLines = lines.filter(line => line.trim() !== '');
      const filteredMarkdown = nonEmptyLines.join(' ');
      emitter.emit(
        BUS_EVENTS.INSERT_INTO_RICH_EDITOR,
        `[${filteredMarkdown}](${url})`
      );

      useTrack(CONVERSATION_EVENTS.INSERT_ARTICLE_LINK);
    },
    toggleQuotedReply() {
      if (!this.isAnEmailChannel) {
        return;
      }

      const nextValue = !this.quotedReplyPreference;
      this.setQuotedReplyFlagForInbox(this.channelType, nextValue);
    },
    shouldIncludeQuotedEmail() {
      return (
        this.quotedReplyPreference &&
        this.shouldShowQuotedReplyToggle &&
        !!this.quotedEmailText
      );
    },
    getMessageWithQuotedEmailText(message) {
      if (!this.shouldIncludeQuotedEmail()) {
        return message;
      }

      const quotedText = this.quotedEmailText || '';
      const header = buildQuotedEmailHeader(
        this.lastEmailWithQuotedContent,
        this.currentContact,
        this.inbox
      );

      return appendQuotedTextToMessage(message, quotedText, header);
    },
    resetRecorderAndClearAttachments() {
      // Reset audio recorder UI state
      this.resetAudioRecorderInput();
      // Reset attached files
      this.attachedFiles = [];
    },
    saveDraft(conversationId, replyType) {
      if (this.message || this.message === '') {
        const key = this.getDraftKey(conversationId, replyType);
        const draftToSave = trimContent(this.message || '', this.maxLength);

        this.$store.dispatch('draftMessages/set', {
          key,
          message: draftToSave,
        });
      }
    },
    setToDraft(conversationId, replyType) {
      this.saveDraft(conversationId, replyType);
      this.message = '';
    },
    getFromDraft() {
      if (this.conversationIdByRoute) {
        const key = this.getDraftKey();
        const messageFromStore =
          this.$store.getters['draftMessages/get'](key) || '';

        // ensure that the message has signature set based on the ui setting
        this.message = this.toggleSignatureForDraft(messageFromStore);
      }
    },
    toggleSignatureForDraft(message) {
      if (this.isPrivate) {
        return message;
      }

      // Even when editor is disabled (e.g. WhatsApp/API can't reply), we must
      // still normalize stale signatures out of drafts when signature is off.
      if (this.isEditorDisabled && this.sendWithSignature) {
        return message;
      }

      const effectiveChannelType = getEffectiveChannelType(
        this.channelType,
        this.inbox?.medium || ''
      );

      return this.sendWithSignature
        ? appendSignature(message, this.messageSignature, effectiveChannelType)
        : removeSignature(message, this.messageSignature, effectiveChannelType);
    },
    removeFromDraft() {
      if (this.conversationIdByRoute) {
        const key = this.getDraftKey();
        this.$store.dispatch('draftMessages/delete', { key });
      }
    },
    getElementToBind() {
      return this.replyEditor;
    },
    getKeyboardEvents() {
      return {
        Escape: {
          action: () => {
            this.hideEmojiPicker();
          },
          allowOnFocusedInput: true,
        },
        '$mod+KeyK': {
          action: e => {
            e.preventDefault();
            const ninja = document.querySelector('ninja-keys');
            ninja.open();
          },
          allowOnFocusedInput: true,
        },
        Enter: {
          action: e => {
            if (this.isAValidEvent('enter')) {
              this.onSendReply();
              e.preventDefault();
            }
          },
          allowOnFocusedInput: true,
        },
        '$mod+Enter': {
          action: () => {
            if (this.copilot.isActive.value && this.isFocused) {
              this.onSubmitCopilotReply();
            } else if (this.isAValidEvent('cmd_enter')) {
              this.onSendReply();
            }
          },
          allowOnFocusedInput: true,
        },
      };
    },
    isAValidEvent(selectedKey) {
      return (
        !this.showUserMentions &&
        !this.showMentions &&
        !this.showCannedMenu &&
        !this.showVariablesMenu &&
        this.isFocused &&
        this.isEditorHotKeyEnabled(selectedKey)
      );
    },
    onPaste(e) {
      // Don't handle paste if compose new conversation modal is open
      if (this.newConversationModalActive) return;

      // Don't handle paste if editor is disabled
      if (this.isEditorDisabled) return;
      if (!this.showFileUpload && !this.isOnPrivateNote) return;

      // Filter valid files (non-zero size)
      Array.from(e.clipboardData.files)
        .filter(file => file.size > 0)
        .filter(file => {
          const isAllowed = isFileTypeAllowedForChannel(file, {
            channelType: this.channelType || this.inbox?.channel_type,
            medium: this.inbox?.medium,
            conversationType: this.conversationType,
            isInstagramChannel: this.isAnInstagramChannel,
            isOnPrivateNote: this.isOnPrivateNote,
          });

          if (!isAllowed) {
            useAlert(
              this.$t('CONVERSATION.FILE_TYPE_NOT_SUPPORTED', {
                fileName: file.name,
              })
            );
          }

          return isAllowed;
        })
        .forEach(file => {
          const { name, type, size } = file;
          this.onFileUpload({ name, type, size, file });
        });
    },
    toggleUserMention(currentMentionState) {
      this.showUserMentions = currentMentionState;
    },
    toggleCannedMenu(value) {
      this.showCannedMenu = value;
    },
    toggleVariablesMenu(value) {
      this.showVariablesMenu = value;
    },
    openWhatsappTemplateModal() {
      if (this.isSendingWhatsAppTemplate) return;
      this.showWhatsAppTemplatesModal = true;
    },
    hideWhatsappTemplatesModal() {
      if (this.isSendingWhatsAppTemplate) return;
      this.showWhatsAppTemplatesModal = false;
    },
    openContentTemplateModal() {
      this.showContentTemplatesModal = true;
    },
    hideContentTemplatesModal() {
      this.showContentTemplatesModal = false;
    },
    async confirmOnSendReply() {
      if (this.isReplyButtonDisabled) {
        return false;
      }
      if (this.showMentions) {
        return false;
      }

      const copilotAcceptedMessage = this.getCopilotAcceptedMessage();
      const isOnWhatsApp =
        this.isATwilioWhatsAppChannel ||
        this.isAWhatsAppCloudChannel ||
        this.is360DialogWhatsAppChannel;
      // Instagram and TikTok do not support sending text and attachments in the same message.
      // For Instagram, combining them causes duplicate messages due to separate echo events per component.
      // For TikTok, the API rejects messages that mix text and media.
      // To handle both cases, text and attachments are always sent as separate messages.
      const isOnInstagram = this.isAnInstagramChannel;
      const isOnTiktok = this.isATiktokChannel;
      let sendOk = true;
      if ((isOnWhatsApp || isOnInstagram || isOnTiktok) && !this.isPrivate) {
        sendOk = await this.sendMessageAsMultipleMessages(
          this.message,
          copilotAcceptedMessage
        );
      } else {
        const messagePayload = this.getMessagePayload(this.message);
        sendOk = await this.sendMessage(
          messagePayload,
          this.message,
          copilotAcceptedMessage
        );
      }

      if (!this.isPrivate) {
        this.clearEmailField();
      }

      // Only clear editor after a real successful send. On failure (e.g. 422),
      // keep draft/suggestion so the agent can fix and retry.
      if (sendOk !== false) {
        this.clearMessage();
      }
      this.hideEmojiPicker();
      return sendOk !== false;
    },
    async sendMessageAsMultipleMessages(message, copilotAcceptedMessage = '') {
      const messages = this.getMultipleMessagesPayload(message);
      const results = await Promise.all(
        messages.map(messagePayload =>
          this.sendMessage(
            messagePayload,
            messagePayload.message || '',
            copilotAcceptedMessage
          )
        )
      );
      return results.every(result => result !== false);
    },
    sendMessageAnalyticsData(
      isPrivate,
      { editorMessage = '', copilotAcceptedMessage = '' } = {}
    ) {
      const normalizeForComparison = message => {
        let normalizedMessage = message || '';

        if (this.sendWithSignature && this.messageSignature && !isPrivate) {
          const effectiveChannelType = getEffectiveChannelType(
            this.channelType,
            this.inbox?.medium || ''
          );
          normalizedMessage = removeSignature(
            normalizedMessage,
            this.messageSignature,
            effectiveChannelType
          );
        }

        return trimContent(normalizedMessage);
      };

      const normalizedAcceptedMessage = normalizeForComparison(
        copilotAcceptedMessage
      );
      const normalizedEditorMessage = normalizeForComparison(editorMessage);

      if (normalizedAcceptedMessage && normalizedEditorMessage) {
        useTrack(CAPTAIN_EVENTS.AI_ASSISTED_MESSAGE_SENT, {
          conversationId: this.conversationIdByRoute,
          channelType: this.channelType,
          editedBeforeSend:
            normalizedAcceptedMessage !== normalizedEditorMessage,
          isPrivate,
        });
      }

      // Analytics data for message signature is enabled or not in channels
      return isPrivate
        ? useTrack(CONVERSATION_EVENTS.SENT_PRIVATE_NOTE)
        : useTrack(CONVERSATION_EVENTS.SENT_MESSAGE, {
            channelType: this.channelType,
            signatureEnabled: this.sendWithSignature,
            hasReplyTo: !!this.inReplyTo?.id,
          });
    },
    async onSendReply() {
      const undefinedVariables = getUndefinedVariablesInMessage({
        message: this.message,
        variables: this.messageVariables,
      });
      if (undefinedVariables.length > 0) {
        const undefinedVariablesCount =
          undefinedVariables.length > 1 ? undefinedVariables.length : 1;
        this.undefinedVariableMessage = this.$t(
          'CONVERSATION.REPLYBOX.UNDEFINED_VARIABLES.MESSAGE',
          {
            undefinedVariablesCount,
            undefinedVariables: undefinedVariables.join(', '),
          }
        );

        const ok = await this.$refs.confirmDialog.showConfirmation();
        if (!ok) return false;
        return this.confirmOnSendReply();
      }
      return this.confirmOnSendReply();
    },
    async sendMessage(
      messagePayload,
      editorMessage = '',
      copilotAcceptedMessage = ''
    ) {
      const payload = this.withCaptainSuggestion(
        messagePayload,
        editorMessage || messagePayload.message
      );
      try {
        await this.$store.dispatch('createPendingMessageAndSend', payload);
        emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
        emitter.emit(BUS_EVENTS.MESSAGE_SENT);
        this.removeFromDraft();
        this.sendMessageAnalyticsData(messagePayload.private, {
          editorMessage,
          copilotAcceptedMessage,
        });
        return true;
      } catch (error) {
        const errorMessage =
          error?.response?.data?.error || this.$t('CONVERSATION.MESSAGE_ERROR');
        useAlert(errorMessage);
        return false;
      }
    },
    async onSendWhatsAppReply(messagePayload) {
      if (this.isSendingWhatsAppTemplate) return false;
      const conversationId = this.currentChat.id;
      this.whatsAppTemplateRequestId += 1;
      const requestId = this.whatsAppTemplateRequestId;
      this.isSendingWhatsAppTemplate = true;
      try {
        const sent = await this.sendMessage({
          conversationId,
          ...messagePayload,
        });
        if (
          sent &&
          this.whatsAppTemplateRequestId === requestId &&
          this.currentChat.id === conversationId
        ) {
          this.showWhatsAppTemplatesModal = false;
        }
        return sent;
      } finally {
        if (this.whatsAppTemplateRequestId === requestId) {
          this.isSendingWhatsAppTemplate = false;
        }
      }
    },
    async onSendContentTemplateReply(messagePayload) {
      this.sendMessage({
        conversationId: this.currentChat.id,
        ...messagePayload,
      });
      this.hideContentTemplatesModal();
    },
    // SiteUp: "Enviar" no card de sugestão — joga o texto no editor e envia direto.
    // onSuccess só roda se o envio for OK; se o canal rejeitar (ex.: comprimento),
    // o card NÃO limpa a sugestão em silêncio.
    handleSendSuggestion(message, onSuccess, telemetry) {
      const accepted = this.replaceText(message);
      if (accepted === false) return;
      this.rememberUsedCaptainSuggestion(message, telemetry);
      // replaceText seta this.message após 100ms (assinatura/variáveis) → envia em seguida.
      setTimeout(async () => {
        const sent = await this.onSendReply();
        if (sent !== false && typeof onSuccess === 'function') {
          onSuccess();
        }
      }, 250);
    },
    rememberShownCaptainSuggestion(telemetry) {
      this.shownCaptainSuggestion = telemetry || null;
    },
    rememberUsedCaptainSuggestion(text, telemetry) {
      if (telemetry?.suggestion_id) {
        this.captainSuggestion = { text, ...telemetry };
        return;
      }
      this.captainSuggestion = {
        text,
        suggestion_id: suggestionIdFromText(text),
        assistant_id: assistantIdFromConversation(this.currentChat),
        generated_at: new Date().toISOString(),
      };
    },
    useCaptainSuggestion(text, telemetry) {
      const accepted = this.replaceText(text);
      if (accepted === false) return;
      this.rememberUsedCaptainSuggestion(text, telemetry);
    },
    clearCaptainSuggestionState() {
      this.captainSuggestion = null;
      this.shownCaptainSuggestion = null;
    },
    sentTextForSuggestionCompare(sentText) {
      let text = sentText || '';
      if (this.sendWithSignature && this.messageSignature && !this.isPrivate) {
        const effectiveChannelType = getEffectiveChannelType(
          this.channelType,
          this.inbox?.medium || ''
        );
        text = removeSignature(
          text,
          this.messageSignature,
          effectiveChannelType
        );
      }
      return text;
    },
    withCaptainSuggestion(messagePayload, sentText) {
      const compareText = this.sentTextForSuggestionCompare(sentText);
      if (
        !String(compareText)
          .replace(/<[^>]*>/g, '')
          .trim()
      ) {
        return messagePayload;
      }
      const captainSuggestion = buildCaptainSuggestionAttribute({
        usedMeta: this.captainSuggestion,
        shownMeta: this.shownCaptainSuggestion,
        sentText: compareText,
      });
      if (!captainSuggestion) return messagePayload;
      return {
        ...messagePayload,
        contentAttributes: {
          ...messagePayload.contentAttributes,
          captain_suggestion: captainSuggestion,
        },
      };
    },
    replaceText(message) {
      // SiteUp: não sobrescrever em silêncio o que o atendente já digitou
      // ao "Usar" uma sugestão da IA.
      if (
        this.hasMeaningfulEditorContent &&
        !window.confirm(this.$t('CONVERSATION.REPLYBOX.REPLACE_DRAFT_CONFIRM'))
      ) {
        return false;
      }
      // era `this.private` (undefined) — a assinatura vazava pra nota privada
      if (this.sendWithSignature && !this.isPrivate) {
        // if signature is enabled, append it to the message
        // appendSignature ensures that the signature is not duplicated
        // so we don't need to check if the signature is already present
        const effectiveChannelType = getEffectiveChannelType(
          this.channelType,
          this.inbox?.medium || ''
        );
        message = appendSignature(
          message,
          this.messageSignature,
          effectiveChannelType
        );
      }

      const updatedMessage = replaceVariablesInMessage({
        message,
        variables: this.messageVariables,
      });

      setTimeout(() => {
        useTrack(CONVERSATION_EVENTS.INSERTED_A_CANNED_RESPONSE);
        this.message = updatedMessage;
      }, 100);
      return true;
    },
    async attachCannedFiles(files) {
      if (!files || !files.length) return;

      const { attached, failed } =
        await materializeCannedFilesForComposer(files);

      // Same channel type gate as paste/upload: rejected files never enter the composer.
      const channelOptions = {
        channelType: this.channelType || this.inbox?.channel_type,
        medium: this.inbox?.medium,
        conversationType: this.conversationType,
        isInstagramChannel: this.isAnInstagramChannel,
        isOnPrivateNote: this.isOnPrivateNote,
      };
      const { accepted, rejected } = filterCannedUploadsForChannel(
        attached,
        channelOptions
      );

      accepted.forEach(upload => {
        this.onFileUpload(upload);
      });

      rejected.forEach(upload => {
        useAlert(
          this.$t('CONVERSATION.FILE_TYPE_NOT_SUPPORTED', {
            fileName: upload.name || upload.file?.name || '',
          })
        );
      });

      if (!failed.length) return;

      // Never pretend failed materializations were attached; surface a visible alert.
      if (failed.length === files.length) {
        useAlert(this.$t('CONVERSATION.REPLYBOX.CANNED_ATTACH_FAILED'));
        return;
      }

      useAlert(
        this.$t('CONVERSATION.REPLYBOX.CANNED_ATTACH_PARTIAL_FAILED', {
          n: failed.length,
        })
      );
    },
    onMobileComposerAction(action) {
      if (action === 'note') {
        this.setReplyMode(REPLY_EDITOR_MODES.NOTE);
        return;
      }
      if (action === 'reply') {
        this.setReplyMode(REPLY_EDITOR_MODES.REPLY);
        return;
      }
      if (action === 'emoji') {
        this.toggleEmojiPicker();
        return;
      }
      if (action === 'audio') {
        this.toggleAudioRecorder();
        return;
      }
      if (action === 'quoted_reply') {
        this.toggleQuotedReply();
        return;
      }
      if (action === 'whatsapp_templates') {
        this.openWhatsappTemplateModal();
        return;
      }
      if (action === 'content_templates') {
        this.openContentTemplateModal();
        return;
      }
      if (action === 'insert_article') {
        this.toggleInsertArticle();
      }
    },
    setReplyMode(mode = REPLY_EDITOR_MODES.REPLY) {
      // Clear attachments when switching between private note and reply modes
      // This is to prevent from breaking the upload rules
      if (this.attachedFiles.length > 0) this.attachedFiles = [];

      const { can_reply: canReply } = this.currentChat;
      this.$store.dispatch('draftMessages/setReplyEditorMode', {
        mode,
      });
      if (canReply || this.isAWhatsAppChannel || this.isAPIInbox)
        this.replyType = mode;
      if (this.isRecordingAudio) {
        this.toggleAudioRecorder();
      }
    },
    clearEditorSelection() {
      this.updateEditorSelectionWith = '';
    },
    addIntoEditor(content) {
      this.updateEditorSelectionWith = content;
      this.onFocus();
    },
    executeCopilotAction(action, data) {
      // SiteUp (Lia): as ações de imóveis abrem o modal de cards, não o fluxo de
      // sugestão de texto do Copilot.
      if (
        action === 'suggest_property_lia' ||
        action === 'suggest_property_todas'
      ) {
        this.propertySource =
          action === 'suggest_property_lia' ? 'lia' : 'todas';
        return;
      }
      this.copilot.execute(action, data);
    },
    openPropertySuggestions(source) {
      if (!PROPERTY_SOURCES.includes(source)) return;
      // O modal insere o imóvel pelo INSERT_INTO_RICH_EDITOR e quem escuta é o
      // WootMessageEditor, que fica desmontado enquanto o painel do copilot ocupa
      // o rodapé. Sem editor na tela a inserção morre calada (o modal ainda avisa
      // "imóvel inserido"), então o painel sai da frente antes de abrir o modal.
      // Com geração em curso o painel não sai, e aí nem abrimos.
      if (this.copilot.isGenerating.value) return;
      this.copilot.showEditor.value = false;
      this.propertySource = source;
    },
    clearMessage() {
      this.message = '';
      this.clearCaptainSuggestionState();
      this.clearCopilotAcceptedMessage();
      if (this.sendWithSignature && !this.isPrivate) {
        // if signature is enabled, append it to the message
        const effectiveChannelType = getEffectiveChannelType(
          this.channelType,
          this.inbox?.medium || ''
        );
        this.message = appendSignature(
          this.message,
          this.messageSignature,
          effectiveChannelType
        );
      }
      this.attachedFiles = [];
      this.isRecordingAudio = false;
      this.resetReplyToMessage();
      this.resetAudioRecorderInput();
    },
    clearEmailField() {
      this.ccEmails = '';
      this.bccEmails = '';
      this.toEmails = '';
    },

    toggleEmojiPicker() {
      this.showEmojiPicker = !this.showEmojiPicker;
    },
    toggleAudioRecorder() {
      this.isRecordingAudio = !this.isRecordingAudio;
      if (!this.isRecordingAudio) {
        this.resetAudioRecorderInput();
      }
    },
    toggleAudioRecorderPlayPause() {
      if (!this.$refs.audioRecorderInput) return;
      if (!this.recordingAudioState) {
        this.$refs.audioRecorderInput.stopRecording();
      } else {
        this.$refs.audioRecorderInput.playPause();
      }
    },
    hideEmojiPicker() {
      if (this.showEmojiPicker) {
        this.toggleEmojiPicker();
      }
    },
    onTypingOn() {
      this.toggleTyping('on');
    },
    onTypingOff() {
      this.toggleTyping('off');
    },
    onBlur() {
      this.isFocused = false;
      this.saveDraft(this.conversationIdByRoute, this.replyType);
    },
    // pointerdown/up no próprio .reply-box: mantém a barra de modo montada e
    // clicável durante o clique inteiro. Só o pointerUP solta a trava, e ele
    // é escutado na janela porque a pessoa pode soltar o botão fora do alvo.
    onComposerPointerDown() {
      // Dedupe: multi-toque agenda um `once` por dedo, e o primeiro `up`
      // soltava a trava com outro dedo ainda apertado (achado do review).
      if (this.isPointerInside) return;
      this.isPointerInside = true;
      window.addEventListener('pointerup', this.onComposerPointerUp, {
        once: true,
      });
      // `pointercancel` é obrigatório: scroll em touch e drag do sistema
      // cancelam o gesto SEM disparar pointerup — a barra ficava "grudada"
      // aberta até o próximo clique em qualquer lugar da janela.
      window.addEventListener('pointercancel', this.onComposerPointerUp, {
        once: true,
      });
    },
    onComposerPointerUp() {
      this.isPointerInside = false;
      window.removeEventListener('pointerup', this.onComposerPointerUp);
      window.removeEventListener('pointercancel', this.onComposerPointerUp);
    },
    onFocus() {
      this.isFocused = true;
    },
    onRecordProgressChanged(duration) {
      this.recordingAudioDurationText = duration;
    },
    onFinishRecorder(file) {
      this.recordingAudioState = 'stopped';
      this.hasRecordedAudio = true;
      // Added a new key isVoiceMessage to the file to identify recorded audio
      // Because to filter and show only non recorded audio and other attachments
      const autoRecordedFile = {
        ...file,
        isVoiceMessage: true,
      };
      return file && this.onFileUpload(autoRecordedFile);
    },
    onRecordError() {
      this.toggleAudioRecorder();
      useAlert(this.$t('CONVERSATION.REPLYBOX.AUDIO_CONVERSION_FAILED'));
    },
    toggleTyping(status) {
      const conversationId = this.currentChat.id;
      const isPrivate = this.isPrivate;

      if (!conversationId) {
        return;
      }

      this.$store.dispatch('conversationTypingStatus/toggleTyping', {
        status,
        conversationId,
        isPrivate,
      });
    },
    attachFile({ blob, file }) {
      if (!this.showFileUpload && !this.isOnPrivateNote) return;

      const reader = new FileReader();
      reader.readAsDataURL(file.file);
      reader.onloadend = () => {
        this.attachedFiles.push({
          currentChatId: this.currentChat.id,
          resource: blob || file,
          isPrivate: this.isPrivate,
          thumb: reader.result,
          blobSignedId: blob ? blob.signed_id : undefined,
          isVoiceMessage: file?.isVoiceMessage || false,
        });
      };
    },
    removeAttachment(attachments) {
      this.attachedFiles = attachments;
    },
    setReplyToInPayload(payload) {
      if (this.inReplyTo?.id) {
        return {
          ...payload,
          contentAttributes: {
            ...payload.contentAttributes,
            in_reply_to: this.inReplyTo.id,
          },
        };
      }

      return payload;
    },
    getMultipleMessagesPayload(message) {
      // Instagram / TikTok: never put caption on attachments; text is always a separate message.
      if (this.isAnInstagramChannel || this.isATiktokChannel) {
        const multipleMessagePayload = [];

        if (this.attachedFiles && this.attachedFiles.length) {
          this.attachedFiles.forEach(attachment => {
            const attachedFile = this.globalConfig.directUploadsEnabled
              ? attachment.blobSignedId
              : attachment.resource.file;
            let attachmentPayload = {
              conversationId: this.currentChat.id,
              files: [attachedFile],
              private: false,
              message: '',
              sender: this.sender,
              isVoiceMessage: attachment.isVoiceMessage || false,
            };
            attachmentPayload = this.setReplyToInPayload(attachmentPayload);
            multipleMessagePayload.push(attachmentPayload);
          });
        }

        if (this.message) {
          let messagePayload = {
            conversationId: this.currentChat.id,
            message,
            private: false,
            sender: this.sender,
          };
          messagePayload = this.setReplyToInPayload(messagePayload);
          multipleMessagePayload.push(messagePayload);
        }

        return multipleMessagePayload;
      }

      // WhatsApp and other multi-message channels: shared production helper for caption rules.
      const corePayloads = buildWhatsAppMultipleMessagesPayload({
        message,
        attachedFiles: this.attachedFiles,
        directUploadsEnabled: this.globalConfig.directUploadsEnabled,
      });

      return corePayloads.map((item, index) => {
        const attachment = this.attachedFiles?.[index];
        let payload = {
          conversationId: this.currentChat.id,
          private: false,
          sender: this.sender,
          message: item.message,
          isVoiceMessage: attachment?.isVoiceMessage || false,
        };
        if (item.files) {
          payload.files = item.files;
        }
        return this.setReplyToInPayload(payload);
      });
    },
    getMessagePayload(message) {
      const messageWithQuote = this.getMessageWithQuotedEmailText(message);

      let messagePayload = {
        conversationId: this.currentChat.id,
        message: messageWithQuote,
        private: this.isPrivate,
        sender: this.sender,
      };
      messagePayload = this.setReplyToInPayload(messagePayload);

      if (this.attachedFiles && this.attachedFiles.length) {
        messagePayload.files = [];
        this.attachedFiles.forEach(attachment => {
          if (this.globalConfig.directUploadsEnabled) {
            messagePayload.files.push(attachment.blobSignedId);
            if (attachment.isVoiceMessage) {
              messagePayload.isVoiceMessage = true;
            }
          } else {
            messagePayload.files.push(attachment.resource.file);
          }
        });
      }

      if (this.ccEmails && !this.isOnPrivateNote) {
        messagePayload.ccEmails = this.ccEmails;
      }

      if (this.bccEmails && !this.isOnPrivateNote) {
        messagePayload.bccEmails = this.bccEmails;
      }

      if (this.toEmails && !this.isOnPrivateNote) {
        messagePayload.toEmails = this.toEmails;
      }
      return this.withCaptainSuggestion(messagePayload, message);
    },
    setCcEmails(value) {
      this.bccEmails = value.bccEmails;
      this.ccEmails = value.ccEmails;
    },
    setCCAndToEmailsFromLastChat() {
      const conversationContact = this.currentChat?.meta?.sender?.email || '';
      const { email: inboxEmail, forward_to_email: forwardToEmail } =
        this.inbox;

      const { cc, bcc, to } = getRecipients(
        this.lastEmail,
        conversationContact,
        inboxEmail,
        forwardToEmail
      );

      this.toEmails = to.join(', ');
      this.ccEmails = cc.join(', ');
      this.bccEmails = bcc.join(', ');
    },
    fetchAndSetReplyTo() {
      const replyStorageKey = LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO;
      // Indexado por display_id da conversa, que repete entre contas.
      const replyToMessageId = contaLocalStorage.getFromJsonStore(
        replyStorageKey,
        this.conversationId
      );

      this.inReplyTo = this.currentChat?.messages?.find(message => {
        if (message.id === replyToMessageId) {
          return true;
        }
        return false;
      });
    },
    onReplyToMessage() {
      this.fetchAndSetReplyTo();
      if (this.inReplyTo) {
        this.$nextTick(() => {
          const pos = this.isSignatureEnabledForInbox ? 'start' : 'end';
          this.messageEditor?.focusEditorInputField(pos);
        });
      }
    },
    resetReplyToMessage() {
      const replyStorageKey = LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO;
      contaLocalStorage.deleteFromJsonStore(
        replyStorageKey,
        this.conversationId
      );
      emitter.emit(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE);
    },
    onNewConversationModalActive(isActive) {
      // Issue is if the new conversation modal is open and we drag and drop the file
      // then the file is not getting attached to the new conversation modal
      // and it is getting attached to the current conversation reply box
      // so to fix this we are removing the drag and drop event listener from the current conversation reply box
      // When new conversation modal is open
      this.newConversationModalActive = isActive;
    },
    onSearchPopoverClose() {
      this.showArticleSearchPopover = false;
    },
    toggleInsertArticle() {
      this.showArticleSearchPopover = !this.showArticleSearchPopover;
    },
    resetAudioRecorderInput() {
      this.recordingAudioDurationText = '00:00';
      this.isRecordingAudio = false;
      this.recordingAudioState = '';
      this.hasRecordedAudio = false;
      // Only clear the recorded audio when we click toggle button.
      this.attachedFiles = this.attachedFiles.filter(
        file => !file?.isVoiceMessage
      );
    },
    toggleEditorSize() {
      this.$emit('toggleEditorSize');
      this.$nextTick(() => this.messageEditor?.focusEditorInputField());
    },
    onSubmitCopilotReply() {
      const acceptedMessage = this.copilot.accept();
      this.message = acceptedMessage;
      this.setCopilotAcceptedMessage(acceptedMessage);
    },
    onAiAgentSuggestion(message) {
      // SiteUp: sugestão do agente Captain da conta (endpoint ai_agent_suggestion,
      // botão roxo) abre o painel do copilot com Editar/Enviar/Reportar, em vez de
      // cair direto no editor de resposta.
      this.copilot.showSuggestion(message);
    },
    async onAiAgentGenerate() {
      // SiteUp: gera a sugestão do agente Captain DENTRO do copilot — abre o painel em
      // loading, chama o endpoint ai_agent_suggestion e completa com a sugestão (igual
      // ao Resumir). Fica no ReplyBox, não no menu (que desmonta ao fechar).
      const id = this.currentChat?.id;
      if (!id || this.copilot.isGenerating.value) return;
      this.copilot.showLoading();
      try {
        const response = await AiAgentSuggestionsAPI.suggestUntilReady(id, {
          shouldContinue: () => this.currentChat?.id === id,
        });
        if (!response) {
          this.copilot.reset(false);
          return;
        }

        const { data } = response;
        if (data?.suggestion) {
          this.copilot.showSuggestion(
            data.suggestion,
            data.tips || [],
            data.cards || []
          );
        } else {
          this.copilot.reset(false);
          useAlert('A IA não retornou uma sugestão.');
        }
      } catch (error) {
        this.copilot.reset(false);
        const apiError = error?.response?.data?.error;
        let fallbackError = 'Falha de conexão ao gerar a sugestão.';
        if (error?.code === SUGGESTION_POLLING_TIMEOUT) {
          fallbackError =
            'A sugestão demorou mais que o esperado. Tente novamente.';
        } else if (error?.response) {
          fallbackError =
            'Não consegui gerar a sugestão agora. Tente novamente em instantes.';
        }
        useAlert(apiError || fallbackError);
      }
    },
    async sendCopilotAsBubbles(text) {
      // Cada parágrafo (separado por linha em branco) da sugestão vira uma
      // mensagem separada = uma bolha no WhatsApp.
      const parts = (text || '')
        .replace(/\r\n?/g, '\n')
        .split(/\n\s*\n+/)
        .map(s => s.trim())
        .filter(Boolean);
      if (!parts.length) return;

      await parts.reduce(
        (chain, part) =>
          chain.then(() => this.sendMessage(this.getMessagePayload(part))),
        Promise.resolve()
      );
      this.copilot.reset(false);
    },
    onReportCopilot() {
      this.copilotReportText = this.copilot.generatedContent.value || '';
      this.showCopilotReport = true;
    },
    onCopilotReportSent() {
      this.copilot.reset(false);
    },
  },
};
</script>

<template>
  <ReplyBoxBanner :message="message" :is-on-private-note="isOnPrivateNote" />
  <div
    v-if="isWorkspaceV2 && isSmallScreen && isOnPrivateNote"
    data-testid="mobile-private-note-indicator"
    class="mx-4 mb-2 flex items-center gap-1.5 text-xs font-medium text-n-amber-11"
    role="status"
  >
    <span class="i-ph-lock size-3.5" aria-hidden="true" />
    {{ $t('CONVERSATION.REPLYBOX.PRIVATE_NOTE') }}
  </div>
  <div
    ref="replyEditor"
    class="reply-box"
    data-conversation-composer
    data-testid="conversation-composer"
    :class="replyBoxClass"
    @pointerdown="onComposerPointerDown"
  >
    <!-- SiteUp (CMP-A → T03-B): esta barra só ocupa espaço com o composer em
         foco, com anexo ou com rascunho — colapsa via grid-template-rows.
         Vale em TODA largura desde o PR #355 (antes só no mobile).
         `inert` quando colapsada: o colapso é visual, e sem isso o Tab cai
         em botões invisíveis e o leitor de tela anuncia controles que não
         estão na tela (achado do review do PR #355). -->
    <div
      v-if="!isSmallScreen"
      :class="[
        isWorkspaceV2 ? 'px-1 pt-2' : 'reply-box__mode-collapse',
        {
          'col-span-3 row-start-1 !pt-0 [&>div]:!h-9 [&_button]:!min-h-8':
            isCompactDesktopComposer,
        },
      ]"
      :inert="!isWorkspaceV2 && !isModePanelVisible"
    >
      <ReplyTopPanel
        :modern="isWorkspaceV2"
        :mode="replyType"
        :conversation-id="conversationId"
        :is-reply-restricted="isReplyRestricted"
        :disabled="
          (copilot.isActive.value && copilot.isButtonDisabled.value) ||
          showAudioRecorderEditor
        "
        :is-editor-disabled="isEditorDisabled"
        :is-message-length-reaching-threshold="isMessageLengthReachingThreshold"
        :characters-remaining="charactersRemaining"
        :editor-content="message"
        :has-content="hasMeaningfulEditorContent"
        @set-reply-mode="setReplyMode"
        @toggle-copilot="copilot.toggleEditor"
        @execute-copilot-action="executeCopilotAction"
      />
    </div>
    <EmojiIconPicker
      v-if="showEmojiPicker"
      v-on-clickaway="hideEmojiPicker"
      mode="emoji"
      class="emoji-dialog"
      :class="{
        'emoji-dialog--expanded': isOnExpandedLayout,
        '!left-auto !right-3 max-w-[calc(100%-1.5rem)] before:!left-auto before:!right-4':
          isWorkspaceV2,
      }"
      @select="addIntoEditor($event.value)"
    />
    <PropertySuggestionModal
      v-if="propertySource"
      :conversation-id="conversationId"
      :source="propertySource"
      @close="propertySource = null"
    />
    <ArticleSearchPopover
      v-if="showArticleSearchPopover && connectedPortalSlug"
      :selected-portal-slug="connectedPortalSlug"
      @insert="handleInsert"
      @close="onSearchPopoverClose"
    />
    <Transition
      mode="out-in"
      enter-active-class="transition-all duration-300 ease-out"
      enter-from-class="opacity-0 translate-y-2 scale-[0.98]"
      enter-to-class="opacity-100 translate-y-0 scale-100"
      leave-active-class="transition-all duration-200 ease-in"
      leave-from-class="opacity-100 translate-y-0 scale-100"
      leave-to-class="opacity-0 translate-y-2 scale-[0.98]"
    >
      <div
        :key="copilot.editorTransitionKey.value"
        class="reply-box__top"
        :class="{
          '!mx-0 !mt-0 !mb-1 !px-4 !py-2':
            isWorkspaceV2 && !isSmallScreen && !isCompactDesktopComposer,
          '!m-0 !p-0 col-start-2 row-start-2 min-w-0 self-center [&_.ProseMirror-woot-style]:!min-h-7 [&_.ProseMirror-woot-style]:!py-0':
            isCompactDesktopComposer,
          '!m-0 !p-0 col-start-2 row-start-1 min-w-0 self-center [&_.ProseMirror-woot-style]:!min-h-6 [&_.ProseMirror-woot-style]:!max-h-32 [&_.ProseMirror-woot-style]:!py-0 [&_.ProseMirror-woot-style]:!leading-6':
            isCompactMobileComposer,
        }"
      >
        <ReplyToMessage
          v-if="shouldShowReplyToMessage"
          :message="inReplyTo"
          @dismiss="resetReplyToMessage"
        />
        <ReplyEmailHead
          v-if="showReplyHead && isDefaultEditorMode"
          v-model:cc-emails="ccEmails"
          v-model:bcc-emails="bccEmails"
          v-model:to-emails="toEmails"
        />
        <AudioRecorder
          v-if="showAudioRecorderEditor"
          ref="audioRecorderInput"
          :audio-record-format="audioRecordFormat"
          @recorder-progress-changed="onRecordProgressChanged"
          @finish-record="onFinishRecorder"
          @record-error="onRecordError"
          @play="recordingAudioState = 'playing'"
          @pause="recordingAudioState = 'paused'"
        />
        <CopilotEditorSection
          v-if="copilot.isActive.value && !showAudioRecorderEditor"
          :show-copilot-editor="copilot.showEditor.value"
          :is-generating-content="copilot.isGenerating.value"
          :generated-content="copilot.generatedContent.value"
          :tips="copilot.generatedTips.value"
          :cards="copilot.generatedCards.value"
          :conversation-id="currentChat.id"
          :placeholder="$t('CONVERSATION.FOOTER.COPILOT_MSG_INPUT')"
          @focus="onFocus"
          @blur="onBlur"
          @clear-selection="clearEditorSelection"
          @close="copilot.showEditor.value = false"
          @content-ready="copilot.setContentReady"
          @send="copilot.sendFollowUp"
        />
        <WootMessageEditor
          v-else-if="!showAudioRecorderEditor"
          ref="messageEditor"
          v-model="message"
          :conversation-id="conversationId"
          :editor-id="editorStateId"
          :data-mobile-composer-control="isSmallScreen ? 'field' : null"
          class="input popover-prosemirror-menu"
          :is-private="isOnPrivateNote"
          :placeholder="messagePlaceHolder"
          :title="
            isWorkspaceV2 && !isSmallScreen
              ? $t(
                  isPrivate
                    ? 'CONVERSATION.FOOTER.PRIVATE_MSG_INPUT'
                    : 'CONVERSATION.FOOTER.MSG_INPUT'
                )
              : null
          "
          :update-selection-with="updateEditorSelectionWith"
          :min-height="4"
          :disabled="isEditorDisabled"
          enable-variables
          :variables="messageVariables"
          :signature="messageSignature"
          allow-signature
          :channel-type="channelType"
          :medium="inbox.medium"
          @typing-off="onTypingOff"
          @typing-on="onTypingOn"
          @focus="onFocus"
          @blur="onBlur"
          @toggle-user-mention="toggleUserMention"
          @toggle-canned-menu="toggleCannedMenu"
          @attach-canned-files="attachCannedFiles"
          @toggle-variables-menu="toggleVariablesMenu"
          @clear-selection="clearEditorSelection"
          @execute-copilot-action="executeCopilotAction"
        />

        <AiReportModal
          v-if="showCopilotReport"
          :show="showCopilotReport"
          :conversation-id="currentChat.id"
          source="copilot"
          :ai-text="copilotReportText"
          @sent="onCopilotReportSent"
          @close="showCopilotReport = false"
        />

        <QuotedEmailPreview
          v-if="shouldShowQuotedPreview && isDefaultEditorMode"
          :quoted-email-text="quotedEmailText"
          :preview-text="quotedEmailPreviewText"
          class="mb-2"
          @toggle="toggleQuotedReply"
        />

        <div
          v-if="hasAttachments && isDefaultEditorMode"
          class="bg-transparent py-0 mb-2"
          @paste="onPaste"
        >
          <AttachmentPreview
            class="mt-2"
            :attachments="attachedFiles"
            @remove-attachment="removeAttachment"
          />
        </div>
        <MessageSignatureMissingAlert
          v-if="
            isSignatureEnabledForInbox &&
            !isSignatureAvailable &&
            isDefaultEditorMode
          "
          class="mb-2"
        />
      </div>
    </Transition>

    <!-- SiteUp: sinal da janela de mensagem (24h / 72h grátis de anúncio) — só WhatsApp oficial -->
    <MessagingWindowNotice
      class="col-span-3"
      :class="{ 'row-start-3 !mb-0': isCompactDesktopComposer }"
      :conversation="currentChat"
      :inbox="inbox"
      :can-open-templates="showWhatsappTemplates"
      @open-templates="openWhatsappTemplateModal"
    />

    <Transition
      mode="out-in"
      enter-active-class="transition-all duration-300 ease-out"
      enter-from-class="opacity-0 translate-y-2 scale-[0.98]"
      enter-to-class="opacity-100 translate-y-0 scale-100"
      leave-active-class="transition-all duration-200 ease-in"
      leave-from-class="opacity-100 translate-y-0 scale-100"
      leave-to-class="opacity-0 translate-y-2 scale-[0.98]"
    >
      <CopilotReplyBottomPanel
        v-if="copilot.isActive.value"
        key="copilot-bottom-panel"
        :is-generating-content="copilot.isButtonDisabled.value"
        @submit="onSubmitCopilotReply"
        @cancel="copilot.reset"
        @send-bubbles="sendCopilotAsBubbles(copilot.generatedContent.value)"
        @report="onReportCopilot"
      />
      <ReplyBottomPanel
        v-else
        key="reply-bottom-panel"
        :compact-mobile="isCompactMobileComposer"
        :compact-desktop="isCompactDesktopComposer"
        :modern-composer="isWorkspaceV2"
        :conversation-id="conversationId"
        :enable-multiple-file-upload="enableMultipleFileUpload"
        :enable-whats-app-templates="showWhatsappTemplates"
        :enable-content-templates="showContentTemplates"
        :inbox="inbox"
        :is-on-private-note="isOnPrivateNote"
        :is-recording-audio="isRecordingAudio"
        :is-send-disabled="isReplyButtonDisabled"
        :is-note="isPrivate"
        :is-editor-disabled="isEditorDisabled"
        :disabled="isRecordingAudio || showAudioRecorderEditor"
        :has-content="hasMeaningfulEditorContent"
        :on-file-upload="onFileUpload"
        :on-send="onSendReply"
        :conversation-type="conversationType"
        :recording-audio-duration-text="recordingAudioDurationText"
        :recording-audio-state="recordingAudioState"
        :send-button-text="replyButtonLabel"
        :show-audio-recorder="showAudioRecorder"
        :show-emoji-picker="showEmojiPicker"
        :show-file-upload="showFileUpload"
        :show-quoted-reply-toggle="shouldShowQuotedReplyToggle"
        :quoted-reply-enabled="quotedReplyPreference"
        :toggle-audio-recorder-play-pause="toggleAudioRecorderPlayPause"
        :toggle-audio-recorder="toggleAudioRecorder"
        :toggle-emoji-picker="toggleEmojiPicker"
        :message="message"
        :portal-slug="connectedPortalSlug"
        :new-conversation-modal-active="newConversationModalActive"
        @mobile-mode="onMobileComposerAction"
        @select-whatsapp-template="openWhatsappTemplateModal"
        @select-content-template="openContentTemplateModal"
        @toggle-insert-article="toggleInsertArticle"
        @toggle-quoted-reply="toggleQuotedReply"
        @execute-copilot-action="executeCopilotAction"
      />
    </Transition>

    <WhatsappTemplates
      :inbox-id="inbox.id"
      :show="showWhatsAppTemplatesModal"
      :sending="isSendingWhatsAppTemplate"
      @close="hideWhatsappTemplatesModal"
      @on-send="onSendWhatsAppReply"
      @cancel="hideWhatsappTemplatesModal"
    />

    <ContentTemplates
      :inbox-id="inbox.id"
      :show="showContentTemplatesModal"
      @close="hideContentTemplatesModal"
      @on-send="onSendContentTemplateReply"
      @cancel="hideContentTemplatesModal"
    />

    <woot-confirm-modal
      ref="confirmDialog"
      :title="$t('CONVERSATION.REPLYBOX.UNDEFINED_VARIABLES.TITLE')"
      :description="undefinedVariableMessage"
    />
  </div>
</template>

<style lang="scss" scoped>
.send-button {
  @apply mb-0;
}

// SiteUp V2 (G4): composer desktop herda casca do CMP-A (#341) —
// raio 26, fundo --elev, borda --border (mockup 03). Mobile compacto
// abaixo continua no media query.
.reply-box {
  @apply relative mb-3 mx-4 border border-n-weak rounded-[var(--workspace-radius,26px)] bg-n-alpha-2 overflow-visible focus-within:border-n-blue-9 transition-colors duration-150;

  &.is-private {
    @apply bg-n-solid-amber dark:border-n-amber-3/10 border-n-amber-12/5;
  }
}

// SiteUp (CMP-A → T03-B): repouso compacto agora vale em TODA largura.
// Mockup aprovado (composer-mobile-v2/01-proposta-principal.html no mobile,
// mockup 03 no desktop): em repouso o composer é uma pílula — o toggle
// Responder/Nota só aparece no FOCO (decisão travada 2026-08-09: decidir o
// modo depois de escrever convida o pior erro do produto, nota interna
// entregue ao cliente), e o editor nasce com 1 linha em vez de 5rem.
//
// Colapsa a barra de modo (ReplyTopPanel, 52px) via grid-template-rows —
// técnica que anima altura de conteúdo variável sem max-height "chutado".
// O item de grid precisa de overflow:hidden pra não vazar na transição.
.reply-box__mode-collapse {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows var(--motion-duration-layout)
    var(--motion-ease-standard);

  // O ReplyTopPanel tem h-[3.25rem] fixo na própria raiz — altura explícita
  // vence a track do grid e a barra nunca fecha. Vira auto pra 0fr/1fr
  // controlarem de verdade (achado testando a técnica isolada num harness
  // antes de mexer no componente real).
  :deep(> *) {
    overflow: hidden;
    min-height: 0;
    height: auto !important;
  }
}

.reply-box.is-focused .reply-box__mode-collapse {
  grid-template-rows: 1fr;
}

@media (prefers-reduced-motion: reduce) {
  .reply-box__mode-collapse {
    transition: none;
  }
}

// O editor (WootMessageEditor, classe .input) nasce com min-h-[5rem] fixo
// (ver Editor.vue) — era o maior peso do composer em repouso. 1.25rem ≈ 1
// linha; cresce sozinho até o max-height existente conforme a pessoa digita.
//
// :not(:where(.resizable-editor-wrapper …)) é OBRIGATÓRIO, não estética.
// No desktop o editor vive dentro do ResizableEditorWrapper, cuja altura sai
// de clamp(--editor-min-allowed, --editor-height, --editor-max-allowed) — e
// as três variáveis são INLINE no wrapper, então não dá pra ajustá-las de um
// ancestral. Sem esta exclusão, o min-height fixo daqui ganha do clamp por
// especificidade e o botão "expandir" e o arraste do handle param de crescer
// o editor (regressão real, achada no review do PR #355). O ganho de repouso
// no desktop vem do colapso da barra de modo, não daqui.
:deep(.input .ProseMirror-woot-style):not(
    :where(.resizable-editor-wrapper .ProseMirror-woot-style)
  ) {
  min-height: 1.25rem;
}

// Nota interna: o wash de fundo já existe (bg-n-solid-amber acima) e o botão
// Enviar já fica âmbar (ReplyBottomPanel); aqui só reforça borda +
// placeholder, sem repintar ícone por ícone (risco de depender de classe
// interna do NextButton que pode mudar).
.reply-box.is-private {
  @apply border-n-amber-8/40;

  :deep(.empty-node::before) {
    @apply text-n-amber-11;
  }
}

@media (max-width: 767.98px) {
  .reply-box {
    @apply mx-2;
  }
}

.send-button {
  @apply mb-0;
}

.reply-box__top {
  @apply relative py-0 px-3 -mt-px overflow-visible;
}

/* SiteUp: abre para cima dentro do painel da conversa.
   O -left-80 antigo empurrava o picker sob a lista (overflow:hidden) e
   o menu ficava "escondido"/cortado. */
.emoji-dialog {
  @apply absolute z-[100] bottom-full mb-2 left-0 right-auto top-auto;

  &::before {
    filter: drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.08));
    @apply left-3 -bottom-2 rotate-0;
  }
}

.emoji-dialog--expanded {
  @apply left-0 bottom-full mb-2 absolute z-[100];

  &::before {
    transform: rotate(0deg);
    @apply ltr:left-1 rtl:right-1 -bottom-2;
  }
}
</style>
